import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  GetFullListOfUsers,
  GetImpersonateToken,
  UserFilterParams,
} from "../../../api";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  HStack,
  Heading,
  Divider,
  Stack,
  VStack,
  Tag,
  TagLabel,
  TagCloseButton,
  Text,
  Spinner,
  Center,
  Flex,
  Select,
  IconButton,
  FormControl,
  FormLabel,
  Switch,
  Grid,
  GridItem,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Collapse,
  useDisclosure,
} from "@chakra-ui/react";
import moment from "moment";
import StatContainer from "../../../components/design_library/StatContainer";
import { useDebounce } from "../../hooks";
import { Step } from "../../../components/design_library/Progress/Steps";
import RegeneratePositions from "../../../components/positions/RegeneratePositions";
import { reImportCashFlows } from "../../../api/Cashflow";
import { UpdateUserFeatureFlags } from "../../../api/Users";

// Define types for our data
interface User {
  id: number;
  email: string;
  role: string;
  subscriptionStatus: boolean;
  hasUsedPremiumSubscription: boolean;
  lastActive: string;
  createdAt: string;
  stripeId?: string;
  featureFlags: string[];
}

interface PaginationInfo {
  page: number;
  pageSize: number;
  totalUsers: number;
  totalPages: number;
}

interface UserStatistics {
  activeIn24Hours: number;
  activeIn7Days: number;
  activeIn30Days: number;
  activeIn90Days: number;
  totalSubscribed: number;
  totalNotSubscribed: number;
  totalAdmins: number;
}

interface UsersResponse {
  users: User[];
  pagination: PaginationInfo;
  statistics: UserStatistics;
  filters?: UserFilterParams;
}

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [filter, setFilter] = useState("");
  const [subscribed, setSubscribed] = useState<User[]>([]);
  const [notSubscribed, setNotSubscribed] = useState<User[]>([]);
  const [admins, setAdmins] = useState<User[]>([]);
  const [loading, setLoading] = useState(false); // Changed from true to false
  const [hasLoadedData, setHasLoadedData] = useState(false); // Track if we've ever loaded data
  const [statistics, setStatistics] = useState<UserStatistics>({
    activeIn24Hours: 0,
    activeIn7Days: 0,
    activeIn30Days: 0,
    activeIn90Days: 0,
    totalSubscribed: 0,
    totalNotSubscribed: 0,
    totalAdmins: 0,
  });
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    pageSize: 10,
    totalUsers: 0,
    totalPages: 1,
  });
  const [activeTab, setActiveTab] = useState<
    "all" | "subscribed" | "notSubscribed" | "admins"
  >("all");

  // Filter state
  const [filterParams, setFilterParams] = useState<UserFilterParams>({});
  const { isOpen: isFilterOpen, onToggle: onToggleFilter } = useDisclosure();

  // Date range presets
  const dateRangePresets = [
    { label: "Last 7 days", value: 7 },
    { label: "Last 30 days", value: 30 },
    { label: "Last 90 days", value: 90 },
    { label: "Last 1 year", value: 365 },
    { label: "All time", value: 0 },
  ];

  // Create a function to filter users based on search term
  const filterUsers = useCallback(
    (searchTerm: string) => {
      if (searchTerm) {
        setFilteredUsers(
          users.filter((user) =>
            user.email.toLowerCase().includes(searchTerm.toLowerCase())
          )
        );
      } else {
        setFilteredUsers(users);
      }
    },
    [users]
  );

  // Apply debouncing to our filter function
  const debouncedFilterUsers = useDebounce(filterUsers, 300);

  const getUsers = useCallback(async (
    page: number = 1,
    pageSize: number = 10,
    filters?: UserFilterParams
  ) => {
    setLoading(true);
    try {
      const response = await GetFullListOfUsers(page, pageSize, filters);

      if (response && response.users) {
        const { users, pagination, statistics } = response as UsersResponse;
        
        // Sort users by most recently signed up
        const sortedUsers = [...users].sort((a, b) => {
          return moment(b.createdAt).diff(moment(a.createdAt));
        });
        
        setUsers(sortedUsers);
        setFilteredUsers(sortedUsers);
        setPagination(pagination);
        setStatistics(statistics); // Store statistics from API
        setHasLoadedData(true); // Mark that we've successfully loaded data

        //filter out admins into own list
        const admins = sortedUsers.filter((user) => user.role === "admin");

        //filter out subscribed users into own list
        const subscribed = sortedUsers.filter((user) => user.subscriptionStatus);

        //filter out not subscribed users into own list
        const notSubscribed = sortedUsers.filter((user) => !user.subscriptionStatus);

        setAdmins(admins);
        setSubscribed(subscribed);
        setNotSubscribed(notSubscribed);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Stabilize filterParams reference to prevent infinite loops
  const stableFilterParams = useMemo(() => filterParams, [
    filterParams.email,
    filterParams.role,
    filterParams.subscriptionStatus,
    filterParams.hasUsedPremiumSubscription,
    filterParams.lastActiveBefore,
    filterParams.lastActiveAfter,
    filterParams.createdBefore,
    filterParams.createdAfter,
    filterParams.createdInLastDays,
  ]);

  // Remove automatic loading - only load when pagination or filters change if data has been loaded before
  useEffect(() => {
    if (hasLoadedData) {
      getUsers(pagination.page, pagination.pageSize, stableFilterParams);
    }
  }, [pagination.page, pagination.pageSize, stableFilterParams, getUsers, hasLoadedData]);

  // Apply filter when filter changes
  useEffect(() => {
    debouncedFilterUsers(filter);
  }, [filter, debouncedFilterUsers]);

  const handleTabChange = (
    tab: "all" | "subscribed" | "notSubscribed" | "admins"
  ) => {
    setActiveTab(tab);

    // Apply appropriate filter based on tab
    let newFilters: UserFilterParams = { ...stableFilterParams };

    // Clear subscription status filters first
    delete newFilters.subscriptionStatus;

    switch (tab) {
      case "all":
        // No specific subscription filter
        break;
      case "subscribed":
        newFilters.subscriptionStatus = true;
        break;
      case "notSubscribed":
        newFilters.subscriptionStatus = false;
        break;
      case "admins":
        newFilters.role = "admin";
        break;
    }

    setFilterParams(newFilters);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination({ ...pagination, page: newPage });
    }
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPageSize = parseInt(e.target.value, 10);
    setPagination({ ...pagination, page: 1, pageSize: newPageSize });
  };

  const handleFilterChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    // Handle different input types
    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFilterParams((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFilterParams((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  }, []);

  const handleDateRangePreset = useCallback((days: number) => {
    if (days === 0) {
      // Clear date filters
      const { createdBefore, createdAfter, createdInLastDays, ...rest } =
        stableFilterParams;
      setFilterParams(rest);
    } else {
      setFilterParams((prev) => ({
        ...prev,
        createdInLastDays: days,
        // Clear any specific date range filters
        createdBefore: undefined,
        createdAfter: undefined,
      }));
    }
  }, [stableFilterParams]);

  const clearFilters = () => {
    setFilterParams({});
    setActiveTab("all");
    setFilter("");
  };

  if (loading && !hasLoadedData) {
    return (
      <Box bg="var(--dark-gray)" p={4} borderRadius={8} h="100%">
        <Center flexDirection="column" h="100%">
          <Spinner size="xl" mb="10px" />
          <Text as="h1" size="lg" mb={4}>
            Loading users...
          </Text>
        </Center>
      </Box>
    );
  }

  // Show initial state if no data has ever been loaded
  if (!loading && !hasLoadedData) {
    return (
      <Box bg="var(--dark-gray)" p={4} borderRadius={8} h="100%">
        <Center flexDirection="column" h="100%">
          <Text as="h1" size="lg" mb={4}>
            Click "Load Users" to retrieve user data
          </Text>
          <Button
            colorScheme="blue"
            size="lg"
            onClick={() => getUsers(pagination.page, pagination.pageSize, stableFilterParams)}
            isLoading={loading}
            loadingText="Loading..."
          >
            Load Users
          </Button>
        </Center>
      </Box>
    );
  }

  // Compute stats for the entire user base (not just current page)
  const totalUsers = pagination.totalUsers;
  
  // Use statistics from API instead of local calculations
  const {
    activeIn24Hours,
    activeIn7Days,
    activeIn30Days,
    activeIn90Days,
    totalSubscribed,
    totalNotSubscribed,
    totalAdmins
  } = statistics;

  // Sign-up stats (still calculated locally from current page data)
  const lastWeekSignups = users.filter((user) =>
    moment(user.createdAt).isAfter(moment().subtract(7, "days"))
  ).length;
  const prevWeekSignups = users.filter(
    (user) =>
      moment(user.createdAt).isAfter(moment().subtract(14, "days")) &&
      moment(user.createdAt).isBefore(moment().subtract(7, "days"))
  ).length;
  const weeklyChange =
    prevWeekSignups > 0
      ? ((lastWeekSignups - prevWeekSignups) / prevWeekSignups) * 100
      : 0;

  const lastMonthSignups = users.filter((user) =>
    moment(user.createdAt).isAfter(moment().subtract(1, "months"))
  ).length;
  const prevMonthSignups = users.filter(
    (user) =>
      moment(user.createdAt).isAfter(moment().subtract(2, "months")) &&
      moment(user.createdAt).isBefore(moment().subtract(1, "months"))
  ).length;
  const monthlyChange =
    prevMonthSignups > 0
      ? ((lastMonthSignups - prevMonthSignups) / prevMonthSignups) * 100
      : 0;

  // Check if any filters are active
  const hasActiveFilters = Object.keys(stableFilterParams).length > 0;

  return (
    <Box bg="var(--dark-gray)" p={4} borderRadius={8}>
      <HStack justifyContent={"space-between"} mb="20px">
        <HStack spacing={4} wrap="wrap">
          <StatContainer title="Total Users" stat={totalUsers} type="NUMERIC" />
          <StatContainer
            title="Subscribed"
            stat={totalSubscribed}
            type="NUMERIC"
          />
          <StatContainer
            title="Not Subscribed"
            stat={totalNotSubscribed}
            type="NUMERIC"
          />
          <StatContainer
            title="Admins"
            stat={totalAdmins}
            type="NUMERIC"
          />
          <StatContainer
            title="Active (24h)"
            stat={activeIn24Hours}
            type="NUMERIC"
          />
          <StatContainer
            title="Active (7d)"
            stat={activeIn7Days}
            type="NUMERIC"
          />
          <StatContainer
            title="Active (30d)"
            stat={activeIn30Days}
            type="NUMERIC"
          />
          <StatContainer
            title="Active (90d)"
            stat={activeIn90Days}
            type="NUMERIC"
          />
          <StatContainer
            title="Sign ups (7d)"
            stat={lastWeekSignups}
            type="NUMERIC"
            change={weeklyChange}
            changeSuffix="%"
          />
          <StatContainer
            title="Sign ups (30d)"
            stat={lastMonthSignups}
            type="NUMERIC"
            change={monthlyChange}
            changeSuffix="%"
          />
        </HStack>

        <HStack>
          <Button
            colorScheme="blue"
            size="sm"
            onClick={() => getUsers(pagination.page, pagination.pageSize, stableFilterParams)}
            isLoading={loading}
            loadingText="Loading..."
          >
            Refresh Users
          </Button>
          <RegeneratePositions />
        </HStack>
      </HStack>

      <Flex direction="column" mb={4}>
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <HStack>
            <Button
              colorScheme={activeTab === "all" ? "blue" : "gray"}
              size="sm"
              onClick={() => handleTabChange("all")}
            >
              All
            </Button>
            <Button
              colorScheme={activeTab === "subscribed" ? "blue" : "gray"}
              size="sm"
              onClick={() => handleTabChange("subscribed")}
            >
              Subscribed
            </Button>
            <Button
              colorScheme={activeTab === "notSubscribed" ? "blue" : "gray"}
              size="sm"
              onClick={() => handleTabChange("notSubscribed")}
            >
              Not Subscribed
            </Button>
            <Button
              colorScheme={activeTab === "admins" ? "blue" : "gray"}
              size="sm"
              onClick={() => handleTabChange("admins")}
            >
              Admins
            </Button>
          </HStack>

          <HStack>
            <InputGroup size="md" width="300px">
              <Input
                placeholder="Filter by email"
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  // Email filtering is now handled by the API
                  setFilterParams((prev) => ({
                    ...prev,
                    email: e.target.value || undefined,
                  }));
                }}
              />
            </InputGroup>
            <Button
              size="sm"
              onClick={onToggleFilter}
              colorScheme={hasActiveFilters ? "blue" : "gray"}
            >
              {isFilterOpen ? "Hide Filters" : "Show Filters"}
              {hasActiveFilters &&
                !isFilterOpen &&
                ` (${Object.keys(stableFilterParams).length})`}
            </Button>
          </HStack>
        </Flex>

        {/* Advanced Filters Panel */}
        <Collapse in={isFilterOpen} animateOpacity>
          <Box p={4} bg="var(--bg)" borderRadius="md" mb={4} shadow="md">
            <Flex justifyContent="space-between" mb={4}>
              <Heading size="sm">Advanced Filters</Heading>
              <Button size="xs" onClick={clearFilters} variant="outline">
                Clear All Filters
              </Button>
            </Flex>

            <Grid
              templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
              gap={4}
            >
              {/* Role Filter */}
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">User Role</FormLabel>
                  <Select
                    name="role"
                    value={stableFilterParams.role || ""}
                    onChange={handleFilterChange as any}
                    placeholder="All Roles"
                    size="sm"
                  >
                    <option value="admin">Admin</option>
                    <option value="user">User</option>
                  </Select>
                </FormControl>
              </GridItem>

              {/* Subscription Status */}
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Subscription Status</FormLabel>
                  <Select
                    name="subscriptionStatus"
                    value={
                      stableFilterParams.subscriptionStatus === undefined
                        ? ""
                        : stableFilterParams.subscriptionStatus
                          ? "true"
                          : "false"
                    }
                    onChange={handleFilterChange as any}
                    placeholder="All Statuses"
                    size="sm"
                  >
                    <option value="true">Subscribed</option>
                    <option value="false">Not Subscribed</option>
                  </Select>
                </FormControl>
              </GridItem>

              {/* Has Used Premium */}
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Has Used Premium</FormLabel>
                  <Select
                    name="hasUsedPremiumSubscription"
                    value={
                      stableFilterParams.hasUsedPremiumSubscription === undefined
                        ? ""
                        : stableFilterParams.hasUsedPremiumSubscription
                          ? "true"
                          : "false"
                    }
                    onChange={handleFilterChange as any}
                    placeholder="All Users"
                    size="sm"
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </Select>
                </FormControl>
              </GridItem>

              {/* Sign-up Date Range */}
              <GridItem colSpan={{ base: 1, md: 3 }}>
                <Flex direction="column">
                  <FormLabel fontSize="sm">Sign-up Date Range</FormLabel>
                  <HStack mb={2}>
                    {dateRangePresets.map((preset) => (
                      <Button
                        key={preset.value}
                        size="xs"
                        onClick={() => handleDateRangePreset(preset.value)}
                        colorScheme={
                          stableFilterParams.createdInLastDays === preset.value
                            ? "blue"
                            : "gray"
                        }
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </HStack>
                  <HStack>
                    <FormControl>
                      <FormLabel fontSize="xs">From</FormLabel>
                      <Input
                        type="date"
                        name="createdAfter"
                        value={stableFilterParams.createdAfter || ""}
                        onChange={handleFilterChange}
                        size="sm"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs">To</FormLabel>
                      <Input
                        type="date"
                        name="createdBefore"
                        value={stableFilterParams.createdBefore || ""}
                        onChange={handleFilterChange}
                        size="sm"
                      />
                    </FormControl>
                  </HStack>
                </Flex>
              </GridItem>
            </Grid>
          </Box>
        </Collapse>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <Flex wrap="wrap" gap={2} mb={4}>
            {Object.entries(stableFilterParams).map(([key, value]) => {
              if (value === undefined || value === "") return null;

              let label = key;
              let displayValue = value;

              // Format the filter display
              if (key === "email") label = "Email contains";
              if (key === "role") {
                label = "Role";
                displayValue = value === "admin" ? "Admin" : "User";
              }
              if (key === "subscriptionStatus") {
                label = "Subscription";
                displayValue = value ? "Active" : "Inactive";
              }
              if (key === "hasUsedPremiumSubscription") {
                label = "Has used premium";
                displayValue = value ? "Yes" : "No";
              }
              if (key === "createdInLastDays") {
                label = "Signup within";
                displayValue = `${value} days`;
              }
              if (key === "createdAfter") {
                label = "Signup after";
                displayValue = moment(value as string).format("MMM D, YYYY");
              }
              if (key === "createdBefore") {
                label = "Signup before";
                displayValue = moment(value as string).format("MMM D, YYYY");
              }

              return (
                <Tag key={key} colorScheme="blue" size="md">
                  <TagLabel>
                    {label}: {displayValue}
                  </TagLabel>
                  <TagCloseButton
                    onClick={() => {
                      const newFilters = { ...stableFilterParams };
                      delete newFilters[key as keyof UserFilterParams];
                      setFilterParams(newFilters);
                    }}
                  />
                </Tag>
              );
            })}
          </Flex>
        )}
      </Flex>

      {loading ? (
        <Center py={8}>
          <Spinner size="md" mr={3} />
          <Text>Refreshing data...</Text>
        </Center>
      ) : (
        <>
          {pagination.totalUsers === 0 && hasLoadedData ? (
            <Center py={8}>
              <VStack spacing={4}>
                <Text fontSize="lg" color="gray.500">
                  No users found matching your current filters
                </Text>
                <Button
                  size="sm"
                  onClick={clearFilters}
                  colorScheme="blue"
                  variant="outline"
                >
                  Clear All Filters
                </Button>
              </VStack>
            </Center>
          ) : (
            <TableStructure users={filteredUsers} />
          )}

          {pagination.totalUsers > 0 && (
            <Flex justifyContent="space-between" alignItems="center" mt={4}>
              <HStack>
                <Text>
                  Showing{" "}
                  {Math.min(
                    (pagination.page - 1) * pagination.pageSize + 1,
                    pagination.totalUsers
                  )}{" "}
                  -
                  {Math.min(
                    pagination.page * pagination.pageSize,
                    pagination.totalUsers
                  )}{" "}
                  of {pagination.totalUsers} users
                </Text>
                <Select
                  value={pagination.pageSize}
                  onChange={handlePageSizeChange}
                  size="sm"
                  width="80px"
                  ml={2}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </Select>
                <Text>per page</Text>
              </HStack>

              <HStack>
                <Button
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  isDisabled={pagination.page === 1}
                >
                  First
                </Button>
                <Button
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  isDisabled={pagination.page === 1}
                >
                  Previous
                </Button>
                <Text>
                  Page {pagination.page} of {pagination.totalPages}
                </Text>
                <Button
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  isDisabled={pagination.page === pagination.totalPages}
                >
                  Next
                </Button>
                <Button
                  size="sm"
                  onClick={() => handlePageChange(pagination.totalPages)}
                  isDisabled={pagination.page === pagination.totalPages}
                >
                  Last
                </Button>
              </HStack>
            </Flex>
          )}
        </>
      )}
    </Box>
  );
}

const TableStructure = ({ users }: { users: User[] }) => {
  return (
    <Table
      variant="striped"
      colorScheme="gray"
      size="sm"
      backgroundColor={"var(--bg)"}
    >
      <Thead backgroundColor={"var(--bg)"}>
        <Tr>
          <Th>Role</Th>
          <Th>Email</Th>
          <Th>Last active</Th>
          <Th>Steps taken</Th>
          <Th>Actions</Th>
          <Th>Feature flags</Th>
        </Tr>
      </Thead>
      <Tbody>
        {users.map((user) => (
          <Row key={user.id} {...user} />
        ))}
      </Tbody>
    </Table>
  );
};

const Row = (user: User) => {
  const [clicked, setClicked] = useState(false);
  const [newFlag, setNewFlag] = useState("");
  const [flags, setFlags] = useState<string[]>(user.featureFlags || []);
  const [saving, setSaving] = useState(false);

  const saveFlags = useCallback(async (updated: string[]) => {
    setSaving(true);
    try {
      await UpdateUserFeatureFlags(user.id, updated);
      setFlags(updated);
    } catch (e) {
      // noop; you may add a toast here in future
    } finally {
      setSaving(false);
    }
  }, [user.id]);
  return (
    <Tr>
      <Td fontWeight={"700"}>
        {user.role === "admin" ? (
          <span style={{ color: "red" }}>Admin</span>
        ) : (
          <span style={{ color: "green" }}>User</span>
        )}
      </Td>
      <Td>{user.email}</Td>
      <Td>
        {user.lastActive && moment(user.lastActive).calendar()}
        <Text color="var(--menu-bar)">
          ({user.lastActive && moment(user.lastActive).fromNow()})
        </Text>
      </Td>

      <Td>
        <Stack spacing="0" direction={{ base: "column", md: "row" }}>
          <Step
            cursor="pointer"
            title={`Registered: ${moment(user.createdAt).calendar()}`}
            description=""
            isActive={true}
            isCompleted={true}
            isFirstStep={true}
            isLastStep={false}
          />

          <Step
            cursor="pointer"
            title={"Sub Active"}
            isActive={user.subscriptionStatus === true}
            isCompleted={user.subscriptionStatus === true}
            isFirstStep={false}
            isLastStep={false}
          />

          <Step
            cursor="pointer"
            title={"Sub Canc."}
            isActive={
              user.subscriptionStatus === false &&
              user.hasUsedPremiumSubscription === true
            }
            isCompleted={
              user.subscriptionStatus === false &&
              user.hasUsedPremiumSubscription === true
            }
            isFirstStep={false}
            isLastStep={true}
          />
        </Stack>
      </Td>

      <Td>
        <VStack justifyContent={"flex-start"}>
          <HStack>
            <Button
              size="xs"
              onClick={async () => {
                setClicked(true);
                const token = await GetImpersonateToken(user.id);
                navigator.clipboard.writeText(
                  `${window.location.origin}/23rjfisf4d3-234f4-fdsfnfj4fndc/token?token=${token.token}`
                );

                setTimeout(() => {
                  setClicked(false);
                }, 1000);
              }}
            >
              {clicked ? "Copied link!" : "Impersonate"}
            </Button>
            <Button
              size="xs"
              onClick={async () => {
                setClicked(true);
                const token = await reImportCashFlows(user.id);
              }}
            >
              Reimport Cashflows
            </Button>
          </HStack>
        </VStack>
      </Td>
      <Td>
        <VStack align="start" spacing={2}>
          <HStack wrap="wrap">
            {flags &&
              flags.map((tag) => (
                <Tag key={tag} colorScheme="blue" m={1}>
                  <TagLabel>{tag}</TagLabel>
                  <TagCloseButton
                    onClick={() => {
                      const updated = flags.filter((f) => f !== tag);
                      saveFlags(updated);
                    }}
                  />
                </Tag>
              ))}
          </HStack>
          <HStack>
            <Input
              size="xs"
              placeholder="Add flag"
              value={newFlag}
              onChange={(e) => setNewFlag(e.target.value)}
              width="160px"
            />
            <Button
              size="xs"
              isLoading={saving}
              onClick={() => {
                const nf = newFlag.trim();
                if (!nf) return;
                if (flags.includes(nf)) return setNewFlag("");
                const updated = [...flags, nf];
                setNewFlag("");
                saveFlags(updated);
              }}
            >
              Add
            </Button>
          </HStack>
        </VStack>
      </Td>
    </Tr>
  );
};
